import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ChartDetail() {
  const { chartId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [chart, setChart] = useState(null);
  const [qaList, setQaList] = useState([]);
  const [selectedQA, setSelectedQA] = useState("");

  const load = () => api.get(`/charts/${chartId}`).then((r) => setChart(r.data));
  useEffect(() => { load(); }, [chartId]);

  useEffect(() => {
    if (user && (user.role === "admin" || user.role === "manager")) {
      api.get("/users/qa-list").then((r) => setQaList(r.data));
    }
  }, [user]);

  const assign = async () => {
    if (!selectedQA) return;
    try {
      await api.post(`/charts/${chartId}/assign`, { qa_id: selectedQA });
      toast.success("Assigned to QA");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (!chart) return <div className="p-10 text-slate-500">Loading…</div>;

  const canAssign = (user?.role === "admin" || user?.role === "manager") && chart.status !== "completed";

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="chart-detail-page">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4">
        <ArrowLeft size={14}/> Back
      </button>
      <PageHeader title={`Chart ${chart.chart_id}`} subtitle={`Patient ${chart.patient_name} · ${chart.assessment_type} · ${chart.assessment_date}`} />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-6 space-y-5">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">M1021 — Primary Diagnosis</div>
            <div>
              {chart.m1021_primary ? (
                <span className="bg-sky-50 text-sky-700 text-sm font-mono rounded px-2 py-0.5">{chart.m1021_primary}</span>
              ) : "—"}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">M1023 — Other Diagnoses</div>
            <div className="grid sm:grid-cols-2 gap-1.5">
              {(chart.m1023_codes || []).length ? chart.m1023_codes.map((row) => (
                <div key={row.slot} className="text-sm font-mono">
                  <span className="text-slate-500 mr-2">{row.slot}</span>
                  <span className="bg-slate-100 text-slate-700 rounded px-1.5 py-0.5">{row.code}</span>
                </div>
              )) : "—"}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Coder notes</div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{chart.notes || "—"}</p>
          </div>

          {chart.audit && (
            <div className="border-t border-slate-100 pt-5" data-testid="audit-summary">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">QA Audit by {chart.audit.qa_name}</div>
              <div className="flex flex-wrap items-baseline gap-4 mb-3">
                <div>
                  <div className="text-3xl font-heading font-semibold text-sky-700" data-testid="audit-score">{chart.audit.score}%</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Overall</div>
                </div>
                {chart.audit.coding_score != null && (
                  <div data-testid="audit-coding-score">
                    <div className="text-xl font-heading font-medium text-emerald-700">{chart.audit.coding_score}%</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Coding</div>
                  </div>
                )}
                {chart.audit.oasis_score != null && (
                  <div data-testid="audit-oasis-score">
                    <div className="text-xl font-heading font-medium text-indigo-700">{chart.audit.oasis_score}%</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">OASIS</div>
                  </div>
                )}
              </div>

              {(() => {
                const all = chart.audit.errors || [];
                const coding = chart.audit.coding_errors || all.filter((e) => e.section === "coding");
                const oasis = chart.audit.oasis_errors || all.filter((e) => e.section === "oasis");
                const untagged = all.filter((e) => !e.section && coding.indexOf(e) === -1 && oasis.indexOf(e) === -1);
                const renderList = (label, list, color) => list.length > 0 && (
                  <div className="mt-3" data-testid={`audit-${label.toLowerCase()}-errors`}>
                    <div className={`text-xs uppercase tracking-wider mb-1 ${color}`}>{label} Errors ({list.length})</div>
                    <ul className="space-y-1.5">
                      {list.map((er, i) => (
                        <li key={i} className="text-sm">
                          <span className="inline-block bg-red-50 text-red-700 border border-red-200 rounded px-2 py-0.5 text-xs font-medium mr-2">{er.category}</span>
                          {er.description}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
                return (
                  <>
                    {renderList("Coding", coding, "text-emerald-700")}
                    {renderList("OASIS", oasis, "text-indigo-700")}
                    {untagged.length > 0 && renderList("Other", untagged, "text-slate-600")}
                  </>
                );
              })()}

              {chart.audit.coding_comments && (
                <p className="mt-3 text-sm text-slate-700"><span className="font-medium text-emerald-700">Coding comments: </span>{chart.audit.coding_comments}</p>
              )}
              {chart.audit.oasis_comments && (
                <p className="mt-2 text-sm text-slate-700"><span className="font-medium text-indigo-700">OASIS comments: </span>{chart.audit.oasis_comments}</p>
              )}
              {chart.audit.comments && !chart.audit.coding_comments && !chart.audit.oasis_comments && (
                <p className="mt-3 text-sm text-slate-700 italic">&ldquo;{chart.audit.comments}&rdquo;</p>
              )}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4 h-fit">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">Status</div>
            <div className="text-base font-medium capitalize">{chart.status.replace("_", " ")}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">Coder</div>
            <div className="text-base">{chart.submitted_by_name}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">QA</div>
            <div className="text-base">{chart.assigned_qa_name || "Unassigned"}</div>
          </div>

          {canAssign && (
            <div className="border-t border-slate-100 pt-4">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Assign QA</label>
              <select value={selectedQA} onChange={(e) => setSelectedQA(e.target.value)}
                      data-testid="assign-qa-select"
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 outline-none mb-2">
                <option value="">Select a QA…</option>
                {qaList.map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
              </select>
              <button onClick={assign} disabled={!selectedQA}
                      data-testid="assign-btn"
                      className="w-full bg-sky-700 text-white rounded-lg py-2 text-sm font-medium hover:bg-sky-600 disabled:opacity-60">
                Assign
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
